import React, { FC, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { defineMessages, useIntl, FormattedMessage } from "react-intl";
import { Svg } from "@natera/material/lib/svg";
import DocumentDoneIcon from "@assets/svg/document-done.svg";
import FamilyWithDoctorImage from "@assets/svg/family-with-doctor.svg";
import FamilyWithBabyImage from "@assets/svg/family-with-baby.svg";
import BillingCenterIcon from "@assets/svg/icons/billing-center.svg";

import "./patientEducation.scss";

const messages = defineMessages({
  title: {
    id: "patientEducation.completion.title",
    defaultMessage: "Thanks for your responses!",
  },
  description: {
    id: "patientEducation.completion.description",
    defaultMessage: "Your provider will review them during your appointment.",
  },
  description2: {
    id: "patientEducation.completion.description2",
    defaultMessage:
      "In the meantime, feel free to explore our tests and process below.",
  },

  panoramaTitle: {
    id: "patientEducation.completion.panoramaTitle",
    defaultMessage: "Panorama Noninvasive Prenatal Testing (NIPT)",
  },
  panoramaDescription: {
    id: "patientEducation.completion.panoramaDescription",
    defaultMessage:
      "Panorama™ is a blood-based genetic, prenatal screening test of the pregnant person that screens for common chromosomal conditions that affect a baby's health.",
  },
  horizonTitle: {
    id: "patientEducation.completion.horizonTitle",
    defaultMessage: "Horizon Carrier Screening",
  },
  horizonDescription: {
    id: "patientEducation.completion.horizonDescription",
    defaultMessage:
      "Horizon genetic carrier screening helps couples determine the risk of passing on serious genetic conditions to their child. Carrier screening can be performed either before or during pregnancy.",
  },
  readMore: {
    id: "patientEducation.completion.readMore",
    defaultMessage: "Learn more",
  },
  partnerTestingTitle: {
    id: "patientEducation.completion.partnerTestingTitle",
    defaultMessage: "Why Partner Testing Matters for Your Family's Health",
  },
  partnerTestingDescription: {
    id: "patientEducation.completion.partnerTestingDescription",
    defaultMessage:
      "Testing your partner helps determine the baby's risk and plan next steps if needed. If you're a carrier of one or more genetic conditions and your partner is also a carrier for the same condition, your baby has a 25% chance of being affected.",
  },
  partnerTestingFooter: {
    id: "patientEducation.completion.partnerTestingFooter",
    defaultMessage:
      "Discuss this kind of testing with your healthcare provider.",
  },
  accessibilityTitle: {
    id: "patientEducation.completion.accessibilityTitle",
    defaultMessage: "Talk to a Natera Genetic Counselor,",
  },
  accessibilityDescription: {
    id: "patientEducation.completion.accessibilityDescription",
    defaultMessage:
      "If you have additional questions following the questionnaire, you can schedule a complimentary genetic information session to speak 1:1 with a genetic counselor. <link>Schedule a session</link>",
  },
  insuranceTitle: {
    id: "patientEducation.completion.insuranceTitle",
    defaultMessage: "Learn more about billing for Natera's tests",
  },
  insuranceDescription: {
    id: "patientEducation.completion.insuranceDescription",
    defaultMessage:
      "Natera is committed to making our tests accessible for everyone, independent of financial status. Learn more by visiting the link below.",
  },
  billingInfo: {
    id: "patientEducation.completion.billingInfo",
    defaultMessage: "Billing information",
  },
});

const CompletionPage: FC = () => {
  const intl = useIntl();
  const [searchParams] = useSearchParams();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Easter egg: ?n=0 hides GIS schedule and billing sections
  const hideNav = searchParams.get("n") === "0";

  const scheduleUrl =
    intl.locale === "es"
      ? "https://nateraspanish.as.me/schedule/996c2d25"
      : "https://patientportal.natera.com/services/genetic_information";

  // Memoize to avoid creating new component on every render
  const renderScheduleLink = useCallback(
    (chunks: React.ReactNode) => (
      <a
        href={scheduleUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="patient-education__read-more"
      >
        {chunks}
      </a>
    ),
    [scheduleUrl]
  );

  return (
    <div className="patient-education">
      <div className="patient-education__container patient-education__completion">
        <div className="patient-education__completion-icon">
          <Svg content={DocumentDoneIcon} />
        </div>

        <h1 className="patient-education__completion-title">
          {intl.formatMessage(messages.title)}
        </h1>

        <p className="patient-education__completion-description">
          {intl.formatMessage(messages.description)}
          <br />
          {intl.formatMessage(messages.description2)}
        </p>

        <hr className="patient-education__divider" />

        <div className="patient-education__test-cards">
          <div className="patient-education__test-card">
            <div className="patient-education__family-illustration">
              <Svg content={FamilyWithDoctorImage} />
            </div>
            <div className="patient-education__test-card-content">
              <h3 className="patient-education__test-card-title">
                {intl.formatMessage(messages.panoramaTitle)}
              </h3>
              <p className="patient-education__test-card-description">
                {intl.formatMessage(messages.panoramaDescription)}{" "}
                <a
                  href="https://www.natera.com/womens-health/panorama-nipt-prenatal-screening/patients/#pg-menu-tabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="patient-education__read-more"
                >
                  {intl.formatMessage(messages.readMore)}
                </a>
              </p>
            </div>
          </div>

          <div className="patient-education__test-card">
            <div className="patient-education__family-illustration">
              <Svg content={FamilyWithBabyImage} />
            </div>
            <div className="patient-education__test-card-content">
              <h3 className="patient-education__test-card-title">
                {intl.formatMessage(messages.horizonTitle)}
              </h3>
              <p className="patient-education__test-card-description">
                {intl.formatMessage(messages.horizonDescription)}{" "}
                <a
                  href="https://www.natera.com/womens-health/horizon-advanced-carrier-screening/patients/#pg-menu-tabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="patient-education__read-more"
                >
                  {intl.formatMessage(messages.readMore)}
                </a>
              </p>
            </div>
          </div>
        </div>

        <div className="patient-education__partner-section">
          <h2 className="patient-education__partner-title">
            {intl.formatMessage(messages.partnerTestingTitle)}
          </h2>
          <p className="patient-education__partner-description">
            {intl.formatMessage(messages.partnerTestingDescription)}
            <br />
            {intl.formatMessage(messages.partnerTestingFooter)}
          </p>
        </div>

        {!hideNav && (
          <>
            <hr className="patient-education__divider" />

            <div className="patient-education__accessibility-section">
              <h2 className="patient-education__accessibility-title">
                {intl.formatMessage(messages.accessibilityTitle)}
              </h2>
              <p className="patient-education__accessibility-text">
                <FormattedMessage
                  {...messages.accessibilityDescription}
                  values={{ link: renderScheduleLink }}
                />
              </p>
            </div>

            <div className="patient-education__insurance-section">
              <div className="patient-education__insurance-icon">
                <Svg content={BillingCenterIcon} />
              </div>
              <h3 className="patient-education__insurance-title">
                {intl.formatMessage(messages.insuranceTitle)}
              </h3>
              <p className="patient-education__insurance-description">
                {intl.formatMessage(messages.insuranceDescription)}
              </p>
              <button
                className="patient-education__billing-button"
                onClick={() =>
                  window.open(
                    "https://www.natera.com/womens-health/pricing-billing/",
                    "_blank",
                    "noopener noreferrer"
                  )
                }
              >
                {intl.formatMessage(messages.billingInfo)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompletionPage;
